/**
 * Loading, empty and error states (§8).
 *
 * Gathered in one file so they read the same everywhere. Three rules they all
 * follow, learned from the panels in DragDuro:
 *
 *  - **Empty copy is specific, never generic.** "No cars in this garage yet" is
 *    a state the user can act on; "No data" is a dead end.
 *  - **An error names what failed and offers the way out.** The API's messages
 *    are written to be shown (apps/api/src/errors.ts guarantees no ids or stack
 *    detail in them), so they are shown rather than replaced with "Oops".
 *  - **Only a retryable failure gets a Retry button.** Offering one for a 403 is
 *    a lie: it will fail identically every time.
 */

import type { ReactNode } from 'react';
import { Inbox, RotateCcw, TriangleAlert } from 'lucide-react';

import { ApiError, NetworkError } from '../api/client';
import { LoadingTree } from './primitives';

/**
 * The loading state — the staging tree, not a spinner.
 *
 * `page` is for a load that owns the whole screen and renders *outside* the app
 * shell (the first "loading your garages", before `.svcdue` exists around it).
 * Without it, that state fell back to unstyled text in the top-left corner,
 * because the `.svcdue .state` rules had nothing to attach to. `page` supplies
 * the `.svcdue` scope and centres it in the viewport.
 */
export function Loading({ label = 'Loading…', page }: { label?: string; page?: boolean }) {
  const inner = (
    <div className="state state-loading" role="status">
      <LoadingTree />
      <span>{label}</span>
    </div>
  );

  return page ? (
    <div className="svcdue">
      <div className="page-loading">{inner}</div>
    </div>
  ) : (
    inner
  );
}

export function Empty({ icon, title, hint, action }: { icon?: ReactNode; title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="state state-empty">
      <span className="state-ic">{icon ?? <Inbox size={20} />}</span>
      <b>{title}</b>
      {hint && <span>{hint}</span>}
      {action}
    </div>
  );
}

/**
 * Turn anything thrown into something worth reading.
 *
 * `ApiError.message` is already user-facing by contract. A `NetworkError` is
 * the one case where the server said nothing at all, and it deserves different
 * words — "check your connection" is actionable; "something went wrong" is not.
 * Anything else is a bug in this app, and gets the generic line because its
 * real message would be a stack trace.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof NetworkError) return error.message;
  return 'Something went wrong.';
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  // A network failure is always worth retrying — the request never landed.
  const canRetry = Boolean(onRetry) && (!(error instanceof ApiError) || error.isRetryable);

  return (
    <div className="state state-error" role="alert">
      <span className="state-ic state-ic-bad">
        <TriangleAlert size={20} />
      </span>
      <b>{errorMessage(error)}</b>
      {canRetry && (
        <button className="act act-sm" onClick={onRetry}>
          <RotateCcw size={12} /> Try again
        </button>
      )}
    </div>
  );
}

/**
 * The query-state wrapper the screens use.
 *
 * Order is load → error → empty → content, and it is not arbitrary: TanStack
 * keeps the previous `data` around while refetching, so testing `data` before
 * `isError` would paint stale content over a failure and never show it.
 */
export function QueryState<T>({
  query,
  empty,
  children,
}: {
  query: { data: T | undefined; isPending: boolean; isError: boolean; error: unknown; refetch: () => void };
  empty?: (data: T) => ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.data === undefined) return <Loading />;

  const emptyState = empty?.(query.data);
  if (emptyState) return <>{emptyState}</>;
  return <>{children(query.data)}</>;
}
